import { useId, useRef, useState, type DragEvent } from "react";
import { useModalFocus } from "@/shared/use-modal-focus";
import { usePresence } from "@/shared/use-presence";

type Props = {
  open: boolean;
  keepByDefault: boolean;
  busy?: boolean;
  onCancel(): void;
  onOpen(file: File, keepPdf: boolean): void;
};

export function OpenPdfDialog({
  open,
  keepByDefault,
  busy = false,
  onCancel,
  onOpen,
}: Props) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [keepPdf, setKeepPdf] = useState(keepByDefault);
  const [dragging, setDragging] = useState(false);
  const presence = usePresence(open, 240);
  const dialogRef = useModalFocus<HTMLElement>(open, onCancel);

  if (!presence.present) return null;

  const acceptDroppedFile = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const next = event.dataTransfer.files.item(0);
    if (next) setFile(next);
  };

  return (
    <div
      className="dialog-backdrop"
      data-motion-state={presence.state}
      aria-hidden={!open}
      role="presentation"
    >
      <section
        ref={dialogRef}
        className="dialog-card open-pdf-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${inputId}-title`}
        tabIndex={-1}
      >
        <div className="dialog-heading">
          <div>
            <p className="eyebrow">Open locally</p>
            <h2 id={`${inputId}-title`}>Choose a PDF</h2>
          </div>
          <button
            className="icon-button"
            type="button"
            aria-label="Close"
            onClick={onCancel}
          >
            ×
          </button>
        </div>

        <div
          className={`pdf-drop-zone ${dragging ? "is-dragging" : ""}`}
          onDragEnter={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragOver={(event) => event.preventDefault()}
          onDragLeave={() => setDragging(false)}
          onDrop={acceptDroppedFile}
        >
          <span className="drop-mark" aria-hidden="true">
            +
          </span>
          <strong>{file ? file.name : "Drop a PDF here"}</strong>
          <span>
            {file
              ? "Ready to verify and open in your browser"
              : "or choose one from this computer"}
          </span>
          <input
            ref={inputRef}
            id={inputId}
            type="file"
            accept=".pdf,application/pdf"
            onChange={(event) => setFile(event.target.files?.item(0) ?? null)}
          />
          <button
            className="secondary-button"
            type="button"
            onClick={() => inputRef.current?.click()}
          >
            Choose file
          </button>
        </div>

        <label className="retention-choice">
          <input
            type="checkbox"
            checked={keepPdf}
            onChange={(event) => setKeepPdf(event.target.checked)}
          />
          <span>
            <strong>Keep a copy on this device</strong>
            <small>
              The PDF is stored in this browser for reopening and offline use.
              It is never uploaded by Marginalia.
            </small>
          </span>
        </label>

        <p className="privacy-line">
          Your PDF is read and hashed entirely in this browser.
        </p>

        <div className="dialog-actions">
          <button className="text-button" type="button" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="primary-button"
            type="button"
            disabled={!file || busy}
            onClick={() => file && onOpen(file, keepPdf)}
          >
            {busy ? "Opening…" : "Open PDF"}
          </button>
        </div>
      </section>
    </div>
  );
}
